import { Locale, Scope } from '@headless-workspace/config';
import {
    Repository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { DecoderHelpers } from '@headless-workspace/utils';
import { ProductDetailSlotDTO, ProductDetailUnknownSlotsDTO } from './ProductDetailSlotsDTO';
import {
    DeliveryAndReturnsInfosContentValue,
    mapDeliveryAndReturnsInfosContent,
    mapPaymentMethods,
    PaymentMethodsValue,
} from './ProductDetailSlotsValue';

export interface ProductDetailSlotsSpec {
    getPaymentMethods(locale: string): Promise<Result<PaymentMethodsValue>>;
    getDeliveryAndReturnsInfosContent(locale: string): Promise<Result<DeliveryAndReturnsInfosContentValue>>;
}

export class ProductDetailSlotsRepository extends Repository implements ProductDetailSlotsSpec {
    path = '/content/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    private async getSlots(locale: Locale) {
        const response = await this.datasource.getResource(`${this.path}/slots`, {
            scope: Scope.ProductDetailPage,
            locale,
        });
        const array = ProductDetailUnknownSlotsDTO.parse(response);
        return DecoderHelpers.safeParseArray(array.slots, ProductDetailSlotDTO);
    }

    async getDeliveryAndReturnsInfosContent(locale: Locale) {
        try {
            const slots = await this.getSlots(locale);
            if (slots.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(
                mapDeliveryAndReturnsInfosContent({
                    slots,
                }),
            );
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getPaymentMethods(locale: Locale): Promise<Result<PaymentMethodsValue>> {
        try {
            const slots = await this.getSlots(locale);

            if (slots.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(mapPaymentMethods({ slots }));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
